// PenetapanTab.js — Tab Penetapan Zona KHAS
import React, { useState, useRef, useEffect } from 'react'
import {
  fmtDate, TIPE_KAWASAN,
  STATUS_PENETAPAN, STATUS_PENGAJUAN, statusTagClass, scoreOf
} from './khasConstants'
import { penetapanApi } from './khasApi'
import ScorePanel from './ScorePanel'

export const LS_PENETAPAN = 'zonakhas_penetapan_v1'

const FORM0 = {
  ditetapkan_oleh:'', rencana_peresmian:'',
  tenant:0, halal:0, sehat:0, catatan:''
}

/* ── Success Modal ───────────────────────────────────────── */
function SuccessModal({ rec, onClose, onGoTab }) {
  return (
    <div className="khas-success-overlay">
      <div className="khas-success-box">
        <div className="khas-success-header">
          <button className="khas-success-close" onClick={onClose}>
            {String.fromCharCode(215)}
          </button>
          <div className="khas-success-icon">
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none"
              stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12"/>
            </svg>
          </div>
          <h2>Penetapan Berhasil Diajukan</h2>
          <p>Data penetapan sedang diverifikasi</p>
        </div>
        <div className="khas-success-body">
          <p style={{ fontSize:13.5, color:'var(--khas-muted)', margin:'0 0 18px' }}>
            Data penetapan Anda telah diterima dan akan segera diverifikasi.
          </p>
          <div className="khas-info-grid">
            {[
              ['Kawasan',         rec.nama],
              ['Lokasi',          rec.kota + ', ' + rec.provinsi],
              ['Ditetapkan oleh', rec.ditetapkan_oleh],
              ['Status',          STATUS_PENETAPAN.PROSES],
            ].map(([label, val]) => (
              <div key={label} className="khas-info-card">
                <div className="khas-info-card-label">{label}</div>
                <div className="khas-info-card-value">{val}</div>
              </div>
            ))}
          </div>
          <div className="khas-steps-section">
            <p className="khas-steps-title">Langkah selanjutnya</p>
            {[
              'Verifikasi dokumen dan kelengkapan penetapan',
              'Penetapan disetujui — menunggu penerbitan surat rekomendasi',
              'Surat Rekomendasi dikirimkan ke KDEKS via email',
            ].map((step, i) => (
              <div key={i} className="khas-step-item">
                <span className="khas-step-num">{i+1}.</span>
                <span>{step}</span>
              </div>
            ))}
          </div>
          <div className="khas-modal-actions">
            <button className="khas-btn khas-btn-primary"
              onClick={() => { onGoTab('riwayat'); onClose() }}>
              Lihat Riwayat
            </button>
            <button className="khas-btn khas-btn-outline" onClick={onClose}>
              Tutup
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

/* ── PenetapanTab ────────────────────────────────────────── */
function PenetapanTab({ session, preFillData, submissions = [], onToast, onConfirm, onGoTab }) {
  const formRef = useRef()
  const [selectedId,    setSelectedId]    = useState(null)
  const [form,          setForm]          = useState(FORM0)
  const [docs,          setDocs]          = useState({ halal:null, rekomendasi:null })
  const [successRec,    setSuccessRec]    = useState(null)
  const [penetapanList, setPenetapanList] = useState([])
  const [loading,       setLoading]       = useState(false)

  const set = (f, v) => setForm(p => ({ ...p, [f]: v }))

  const loadPenetapan = async () => {
    try {
      const data = await penetapanApi.getAll()
      setPenetapanList(data.penetapan || [])
    } catch (err) { console.error(err) }
  }

  useEffect(() => { loadPenetapan() }, [])

  const approvedList = submissions.filter(s =>
    s.status === STATUS_PENGAJUAN.DISETUJUI &&
    s.kdeks_wilayah === session.kdeks_wilayah
  )

  const existingPenetapan = penetapanList.find(p =>
    p.pengajuan_id === (preFillData?.id || selectedId)
  )

  useEffect(() => {
    if (approvedList.length === 1 && !selectedId && !preFillData)
      setSelectedId(approvedList[0].id)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [approvedList.length])

  useEffect(() => {
    setForm(FORM0); setDocs({ halal:null, rekomendasi:null })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId, preFillData?.id])

  const selectedSub = preFillData || approvedList.find(s => s.id === selectedId) || null

  const scoreData = selectedSub ? {
    tipe:   selectedSub.tipe,
    tenant: form.tenant,
    halal:  form.halal,
    sehat:  form.sehat,
    k0: !!selectedSub.kriteria?.[0], k1: !!selectedSub.kriteria?.[1],
    k2: !!selectedSub.kriteria?.[2], k3: !!selectedSub.kriteria?.[3],
    k4: !!selectedSub.kriteria?.[4], k5: !!selectedSub.kriteria?.[5],
  } : null

  const handleSubmit = e => {
    e.preventDefault()
    if (!formRef.current.checkValidity()) { formRef.current.reportValidity(); return }
    if (!selectedSub) { onToast('Pilih pengajuan yang akan dilanjutkan'); return }

    // ← Validasi halal dan sehat tidak boleh melebihi tenant
    if (form.halal > form.tenant) {
      onToast('Tenant bersertifikat halal tidak boleh melebihi total tenant')
      return
    }
    if (form.sehat > form.tenant) {
      onToast('Tenant ber-SLHS/IKL tidak boleh melebihi total tenant')
      return
    }

    // ← Validasi scoring — hard block kalau syarat utama belum terpenuhi
    if (scoreData) {
      const sc = scoreOf(scoreData)
      if (sc.blockReason) {
        onToast(sc.blockReason)
        return
      }
      if (!sc.halalOk) {
        onToast(`Persentase sertifikat halal belum memenuhi minimum (${Math.round(sc.config.thresholdHalal)}%)`)
        return
      }
      if (!sc.sehatOk) {
        onToast(`Persentase label sehat belum memenuhi minimum (${Math.round(sc.config.thresholdSehat)}%)`)
        return
      }
    }

    onConfirm({
      title:   'Konfirmasi Penetapan',
      message: 'Apakah data penetapan sudah benar?<br><br>Data yang sudah disubmit <b>tidak dapat diubah</b>.',
      yes:     'Ya, ajukan penetapan',
      onYes:   submitForm,
    })
  }

  const submitForm = async () => {
    setLoading(true)
    try {
      const formData = new FormData()
      formData.append('pengajuan_id',      selectedSub.id)
      formData.append('tenant',            form.tenant)
      formData.append('halal',             form.halal)
      formData.append('sehat',             form.sehat)
      formData.append('ditetapkan_oleh',   form.ditetapkan_oleh)
      formData.append('rencana_peresmian', form.rencana_peresmian)
      formData.append('catatan',           form.catatan || '')

      if (docs.halal)       formData.append('dokumen', docs.halal)
      if (docs.rekomendasi) formData.append('dokumen', docs.rekomendasi)

      const result = await penetapanApi.create(formData)

      setForm(FORM0); setDocs({ halal:null, rekomendasi:null })
      setSuccessRec({
        ...result.penetapan,
        nama:     selectedSub.nama,
        kota:     selectedSub.kota,
        provinsi: selectedSub.provinsi,
      })
      await loadPenetapan()
    } catch (err) {
      onToast(err.message || 'Gagal mengajukan penetapan')
    }
    setLoading(false)
  }

  const REQ = <span style={{ color:'var(--khas-red)' }}>*</span>

  // ── Empty state ──────────────────────────────────────────
  if (approvedList.length === 0 && !preFillData) {
    return (
      <div>
        <div className="khas-card">
          <div className="khas-empty">
            <div style={{ fontSize:36, marginBottom:10, opacity:.4 }}>—</div>
            <b>Belum ada pengajuan yang disetujui</b>
            <p style={{ margin:'8px 0 16px', fontSize:13.5, color:'var(--khas-muted)' }}>
              Ajukan zona KHAS terlebih dahulu dan tunggu persetujuan.
            </p>
            <div style={{ display:'flex', gap:10, justifyContent:'center' }}>
              <button className="khas-btn khas-btn-primary" onClick={() => onGoTab('daftar')}>
                Ajukan Zona KHAS
              </button>
              <button className="khas-btn khas-btn-outline" onClick={() => onGoTab('riwayat')}>
                Lihat Riwayat
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div>
      {successRec && (
        <SuccessModal
          rec={successRec}
          onClose={() => setSuccessRec(null)}
          onGoTab={onGoTab} />
      )}

      <div className="khas-card" style={{ marginBottom:18 }}>
        <h2>Penetapan Zona KHAS</h2>
        <p className="khas-sub" style={{ marginBottom:0 }}>
          <b>Fase rampung</b> — lengkapi data teknis kawasan.
        </p>
      </div>

      {/* Pilih pengajuan jika lebih dari satu */}
      {approvedList.length > 1 && !preFillData && (
        <div className="khas-card" style={{ marginBottom:18 }}>
          <div className="khas-lt" style={{ marginBottom:10 }}>
            Pilih pengajuan yang akan dilanjutkan {REQ}
          </div>
          <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
            {approvedList.map(s => {
              const pt = penetapanList.find(p => p.pengajuan_id === s.id)
              return (
                <label key={s.id} style={{
                  display:'flex', alignItems:'center', gap:12, padding:'12px 16px',
                  borderRadius:9, cursor:'pointer', transition:'all .15s',
                  border:'1.5px solid '+(selectedId===s.id?'var(--khas-green)':'var(--khas-border)'),
                  background: selectedId===s.id ? 'var(--khas-green-l)' : '#fff',
                }}>
                  <input type="radio" name="selectedPengajuan"
                    checked={selectedId===s.id}
                    onChange={() => setSelectedId(s.id)}
                    style={{ accentColor:'var(--khas-green)' }} />
                  <div style={{ flex:1 }}>
                    <div style={{ fontWeight:600, fontSize:14 }}>{s.nama}</div>
                    <div style={{ fontSize:12.5, color:'var(--khas-muted)' }}>
                      {s.kota}, {s.provinsi} · {fmtDate(s.created_at)}
                    </div>
                  </div>
                  {pt && (
                    <span className={'khas-tag '+statusTagClass(pt.status)}
                      style={{ fontSize:11 }}>
                      {pt.status}
                    </span>
                  )}
                </label>
              )
            })}
          </div>
        </div>
      )}

      {/* Status penetapan yang sudah ada */}
      {selectedSub && existingPenetapan && (
        <div className="khas-card">
          <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:14 }}>
            <span className={'khas-tag '+statusTagClass(existingPenetapan.status)}>
              {existingPenetapan.status}
            </span>
          </div>
          {existingPenetapan.status === STATUS_PENETAPAN.PROSES && (
            <div style={{ background:'var(--khas-amber-l)', border:'1px solid #fde68a', borderRadius:10, padding:'16px 18px' }}>
              <div style={{ fontWeight:700, marginBottom:6, color:'var(--khas-amber)' }}>
                Penetapan Sedang Diverifikasi
              </div>
              <p style={{ margin:0, fontSize:13.5, color:'var(--khas-muted)' }}>
                Data penetapan <b>{selectedSub.nama}</b> sedang ditinjau.
              </p>
              <button className="khas-btn khas-btn-outline khas-btn-sm"
                style={{ marginTop:12 }} onClick={() => onGoTab('riwayat')}>
                Lihat Riwayat
              </button>
            </div>
          )}
          {existingPenetapan.status === STATUS_PENETAPAN.DISETUJUI && (
            <div style={{ background:'var(--khas-green-l)', border:'1px solid #a7f3d0', borderRadius:10, padding:'16px 18px' }}>
              <div style={{ fontWeight:700, marginBottom:6, color:'var(--khas-green-d)' }}>
                Penetapan Disetujui
              </div>
              <p style={{ margin:0, fontSize:13.5, color:'var(--khas-muted)' }}>
                Menunggu penerbitan Surat Rekomendasi oleh KNEKS.
              </p>
            </div>
          )}
          {/* ← Fix: warna hijau bukan ungu */}
          {existingPenetapan.status === STATUS_PENETAPAN.SELESAI && (
            <div style={{ background:'#d1fae5', border:'1px solid #a7f3d0', borderRadius:10, padding:'16px 18px' }}>
              <div style={{ fontWeight:700, marginBottom:6, color:'#047857' }}>
                Surat Rekomendasi Telah Diterbitkan
              </div>
              <p style={{ margin:'0 0 10px', fontSize:13.5, color:'var(--khas-muted)' }}>
                Surat Rekomendasi telah dikirimkan ke email KDEKS.
              </p>
              <button className="khas-btn khas-btn-outline khas-btn-sm"
                onClick={() => onGoTab('direktori')}>
                Lihat Direktori
              </button>
            </div>
          )}
          {existingPenetapan.status === STATUS_PENETAPAN.DITOLAK && (
            <div style={{ background:'var(--khas-red-l)', border:'1px solid #fecaca', borderRadius:10, padding:'16px 18px' }}>
              <div style={{ fontWeight:700, marginBottom:6, color:'var(--khas-red)' }}>
                Penetapan Ditolak
              </div>
              {existingPenetapan.alasan_tolak && (
                <div style={{ background:'#fff', borderRadius:7, padding:'8px 12px', marginBottom:12, fontSize:13, color:'var(--khas-red)', border:'1px solid #fecaca' }}>
                  <b>Alasan:</b> {existingPenetapan.alasan_tolak}
                </div>
              )}
              <button className="khas-btn khas-btn-primary"
                onClick={() => onGoTab('daftar')}>
                Ajukan Ulang dari Pengajuan
              </button>
            </div>
          )}
        </div>
      )}

      {/* Form penetapan */}
      {selectedSub && !existingPenetapan && (
        <div className="khas-form-layout">
          <form ref={formRef} onSubmit={handleSubmit}>

            {/* A. Identitas read-only */}
            <fieldset className="khas-fieldset" style={{ marginBottom:16 }}>
              <legend className="khas-legend">A · Identitas Kawasan (dari Pengajuan)</legend>
              <div className="khas-readonly-grid">
                {[
                  ['Nama kawasan',   selectedSub.nama],
                  ['Kota',           selectedSub.kota],
                  ['Provinsi',       selectedSub.provinsi],
                  ['Tipe kawasan',   TIPE_KAWASAN.find(t=>t.value===selectedSub.tipe)?.label||selectedSub.tipe],
                  ['Pengelola',      selectedSub.pic],
                  ['Instansi',       selectedSub.instansi||'—'],
                  ['Kode pengajuan', selectedSub.id],
                  ['Diajukan',       fmtDate(selectedSub.created_at)],
                ].map(([label, val]) => (
                  <div key={label} className="khas-readonly-item">
                    <span className="khas-lt" style={{ marginBottom:4 }}>{label}</span>
                    <div className="khas-readonly-value">{val}</div>
                  </div>
                ))}
              </div>
            </fieldset>

            {/* B. Data Tenant */}
            <fieldset className="khas-fieldset" style={{ marginBottom:16 }}>
              <legend className="khas-legend">B · Data Tenant &amp; Sertifikasi</legend>
              <div className="khas-grid3">
                <label className="khas-fld">
                  <span className="khas-lt">Jumlah tenant {REQ}</span>
                  <input className="khas-input" type="number" min="1" required
                    value={form.tenant}
                    onChange={e => set('tenant', +e.target.value || 0)} />
                </label>
                <label className="khas-fld">
                  <span className="khas-lt">Tenant bersertifikat Halal {REQ}</span>
                  <input className="khas-input" type="number" min="0" required
                    value={form.halal}
                    onChange={e => set('halal', +e.target.value || 0)} />
                  <span className="khas-hint">Tidak boleh melebihi total tenant</span>
                </label>
                <label className="khas-fld">
                  <span className="khas-lt">Tenant ber-SLHS/IKL {REQ}</span>
                  <input className="khas-input" type="number" min="0" required
                    value={form.sehat}
                    onChange={e => set('sehat', +e.target.value || 0)} />
                  <span className="khas-hint">Aman &amp; Sehat — tidak boleh melebihi total tenant</span>
                </label>
              </div>
            </fieldset>

            {/* C. Dokumen */}
            <fieldset className="khas-fieldset" style={{ marginBottom:16 }}>
              <legend className="khas-legend">C · Dokumen Wajib</legend>
              <p className="khas-hint" style={{ marginTop:-2, marginBottom:12 }}>
                Format PDF/JPG/PNG, maks. 5 MB per berkas.
              </p>
              <div className="khas-grid2">
                <label className="khas-fld">
                  <span className="khas-lt">Dokumen Sertifikasi Halal {REQ}</span>
                  <input className="khas-input" type="file"
                    accept=".pdf,.jpg,.jpeg,.png" required
                    onChange={e => setDocs(p => ({ ...p, halal:e.target.files[0]||null }))} />
                  {docs.halal && <span className="khas-hint">{docs.halal.name}</span>}
                </label>
                <label className="khas-fld">
                  <span className="khas-lt">Dokumen SLHS/IKL {REQ}</span>
                  <input className="khas-input" type="file"
                    accept=".pdf,.jpg,.jpeg,.png" required
                    onChange={e => setDocs(p => ({ ...p, rekomendasi:e.target.files[0]||null }))} />
                  {docs.rekomendasi && <span className="khas-hint">{docs.rekomendasi.name}</span>}
                </label>
              </div>
            </fieldset>

            {/* D. Info Penetapan */}
            <fieldset className="khas-fieldset" style={{ marginBottom:16 }}>
              <legend className="khas-legend">D · Info Penetapan</legend>
              <div className="khas-grid2">
                <label className="khas-fld">
                  <span className="khas-lt">Ditetapkan oleh {REQ}</span>
                  <input className="khas-input" value={form.ditetapkan_oleh} required
                    onChange={e => set('ditetapkan_oleh', e.target.value)}
                    placeholder="Nama pejabat / instansi yang menetapkan" />
                </label>
                <label className="khas-fld">
                  <span className="khas-lt">Rencana tanggal peresmian {REQ}</span>
                  <input className="khas-input" type="date" required
                    value={form.rencana_peresmian}
                    min={new Date().toISOString().split('T')[0]}
                    onChange={e => set('rencana_peresmian', e.target.value)} />
                </label>
              </div>
              <label className="khas-fld">
                <span className="khas-lt">Catatan tambahan</span>
                <textarea className="khas-input" value={form.catatan}
                  onChange={e => set('catatan', e.target.value)}
                  placeholder="Keterangan tambahan" />
              </label>
            </fieldset>

            <div style={{ display:'flex', gap:10 }}>
              <button type="submit"
                className="khas-btn khas-btn-primary"
                style={{ fontSize:15, padding:'13px 26px' }}
                disabled={loading}>
                {loading ? 'Mengirim...' : 'Ajukan Penetapan'}
              </button>
              <button type="button" className="khas-btn khas-btn-outline"
                onClick={() => onGoTab('riwayat')} disabled={loading}>
                Kembali ke Riwayat
              </button>
            </div>
          </form>

          {scoreData && (
            <div style={{ position:'sticky', top:84 }}>
              <ScorePanel formData={scoreData} />
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default PenetapanTab